import { SEGMENTOS_EXAMEN } from './examenFisico';

// ── Orden céfalo-caudal del bloque de piel ───────────────────────────────────
//
// Pedido del fundador desde su turno (2026-08-29): "necesito que tegumentario
// se ordene céfalo caudal si son muchos hallazgos".
//
// EL PROBLEMA. Todos los puentes de prosa de la piel escriben en el MISMO campo
// (`tegumentario.nota`): el reparto por sistema, el examen físico, las lesiones
// del mapa corporal y la memoria por sistema. Cada uno APPENDEA, así que el
// bloque queda en orden de llegada, no en orden de cuerpo. Con dos o tres
// hallazgos da lo mismo; con doce, la enfermera lee saltando de la cara al
// periné y de vuelta a las manos, y ese documento se copia a la ficha.
//
// LO QUE ESTA FUNCIÓN NO HACE, y es lo que la hace segura:
//  · No cambia una sola palabra. Es una PERMUTACIÓN de párrafos, fijada por
//    test: el conjunto de líneas que entra es exactamente el que sale.
//  · No parte ni fusiona frases. Un párrafo que cuenta varias zonas viaja
//    entero y se ubica por la PRIMERA que nombra (decisión del fundador).
//  · No mueve lo que no supo clasificar. Las líneas sin zona reconocible se
//    quedan CLAVADAS en su posición y las zonificadas se reordenan entre sí,
//    ocupando los huecos que ya ocupaban. Así una frase general de apertura
//    ("Piel indemne al recibir") no termina en medio del documento.
//
// El ORDEN sale de `SEGMENTOS_EXAMEN`, que ya es el orden céfalo-caudal de la
// casa y el que usa la prosa del examen. Cero catálogo paralelo: si mañana se
// reordenan los segmentos, esto se reordena con ellos.

// Umbral por defecto (decisión del fundador, 2026-08-29): con tres zonas o
// menos el bloque se lee bien igual y moverlo sorprende más de lo que ayuda.
export const MIN_ZONAS_ORDEN = 4;

// PEREZOSO A PROPÓSITO, no por estilo: `examenFisico` y `evolucionSistema` se
// importan en círculo, así que al CARGAR este módulo `SEGMENTOS_EXAMEN` todavía
// puede ser undefined y el `.map` revienta el bundle entero (11 suites caídas
// en la primera versión; la suite por patrón no lo veía porque ahí el orden de
// importación era otro, la completa sí). Al PRIMER uso, en cambio, el catálogo
// ya está listo. No volver a calcular esto en la carga del módulo.

// Vocabulario anatómico por segmento: lo que una enfermera escribe de verdad.
// NO es un catálogo paralelo de datos, es el índice que traduce prosa libre a
// los segmentos que ya existen. Los labels del catálogo entran solos más abajo.
//
// Los conflictos reales están resueltos a propósito y con criterio clínico:
// "codo" es de extremidad superior aunque sea zona de presión; "escápula" es
// espalda; "trocánter" e "isquion" son puntos de apoyo (zona sacra); "talón" es
// extremidad inferior, que es donde el examen lo describe. Y "labios mayores"
// gana sobre "labio" porque, a igual posición, la pista más larga manda.
const PISTAS_SEGMENTO = {
  cabeza: ['cabeza', 'cara', 'craneo', 'cráneo', 'facies', 'frente', 'mejilla', 'menton', 'mentón',
    'cuero cabelludo', 'occipucio', 'occipital', 'pupila', 'pupilas', 'ocular', 'ojo', 'ojos',
    'parpado', 'párpado', 'conjuntiva', 'mucosa', 'mucosas', 'boca', 'oral', 'lengua', 'labio',
    'labios', 'comisura', 'oreja', 'orejas', 'lobulo', 'lóbulo', 'lobulos', 'lóbulos', 'pabellon',
    'pabellón', 'auricular', 'nariz', 'nasal', 'narina'],
  cuello: ['cuello', 'cervical', 'yugular', 'traquea', 'tráquea', 'traqueal', 'traqueostomia',
    'traqueostomía', 'carotida', 'carótida', 'tiroides'],
  torax: ['torax', 'tórax', 'toracico', 'torácico', 'toracica', 'torácica', 'mama', 'mamaria',
    'inframamaria', 'clavicula', 'clavícula', 'claviculas', 'clavículas', 'clavicular',
    'esternon', 'esternón', 'esternal', 'costal', 'intercostal', 'pectoral', 'pleural', 'axila',
    'axilar'],
  abdomen: ['abdomen', 'abdominal', 'ombligo', 'umbilical', 'periumbilical', 'flanco', 'epigastrio',
    'hipogastrio', 'fosa iliaca', 'fosa ilíaca', 'colostomia', 'colostomía', 'ileostomia',
    'ileostomía', 'gastrostomia', 'gastrostomía', 'ostomia', 'ostomía'],
  eess: ['extremidad superior', 'extremidades superiores', 'eess', 'brazo', 'brazos', 'braquial',
    'antebrazo', 'mano', 'manos', 'muñeca', 'muneca', 'codo', 'codos', 'hombro', 'hombros',
    'palmar', 'radial', 'cubital', 'picc', 'picccline', 'piccline'],
  eeii: ['extremidad inferior', 'extremidades inferiores', 'eeii', 'pierna', 'piernas', 'muslo',
    'muslos', 'pie', 'pies', 'tobillo', 'maleolo', 'maléolo', 'rodilla', 'rodillas', 'ortejo',
    'ortejos', 'talon', 'talón', 'talones', 'popliteo', 'poplíteo', 'femoral', 'pedio', 'tibial',
    'pantorrilla', 'gemelo'],
  genitales: ['genital', 'genitales', 'perine', 'periné', 'perineal', 'perianal', 'peri anal',
    'inguinal', 'ingle', 'escroto', 'vulva', 'labios mayores', 'labio mayor', 'pubis', 'pubiano',
    'dai'],
  espalda: ['espalda', 'dorsal', 'dorso', 'escapula', 'escápula', 'escapular', 'columna', 'lumbar',
    'paravertebral', 'interescapular'],
  sacra: ['sacro', 'sacra', 'sacral', 'coxis', 'coccix', 'cóccix', 'isquion', 'isquiatico',
    'isquiático', 'trocanter', 'trocánter', 'gluteo', 'glúteo', 'gluteos', 'glúteos',
    'zona de apoyo', 'zonas de apoyo', 'puntos de apoyo'],
};

const normalizar = (t) => String(t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Los labels de los NUEVE SEGMENTOS entran como pistas sin escribirlos dos
// veces ("Extremidades superiores", "Genitales y periné", "Zona sacra y puntos
// de apoyo"): son anatómicos por definición.
//
// Los labels de SUBSEGMENTO no entran, y esa exclusión no es pereza: la mitad
// nombra una técnica, no una zona ("Piel", "Edema", "Inspección", "Palpación",
// "Otros hallazgos"), y varios se repiten idénticos en segmentos distintos.
// Con ellos dentro, "Piel indemne al recibir, sin lesiones por presión" se
// clasificaba como extremidad superior, porque `eess` es el primer segmento
// que tiene un subsegmento llamado "Piel", y esa frase general terminaba en
// medio del documento. El vocabulario anatómico real vive en PISTAS_SEGMENTO.
function construirIndice() {
  const idx = new Map(); // pista normalizada → segmento id
  const sumar = (segId, frase) => {
    const f = normalizar(frase).replace(/\([^)]*\)/g, ' ').replace(/[^a-z0-9ñ ]/g, ' ').trim();
    if (f.length < 3) return;
    if (!idx.has(f)) idx.set(f, segId);
  };
  for (const seg of SEGMENTOS_EXAMEN) sumar(seg.id, seg.label);
  // Las pistas escritas a mano mandan sobre los labels: son las palabras que
  // de verdad aparecen en la prosa, y resuelven los conflictos de arriba.
  for (const [segId, pistas] of Object.entries(PISTAS_SEGMENTO)) {
    for (const p of pistas) idx.set(normalizar(p), segId);
  }
  return idx;
}
let _cache = null;
function indice() {
  if (!_cache) {
    _cache = { pistas: construirIndice(), orden: SEGMENTOS_EXAMEN.map((sg) => sg.id) };
  }
  return _cache;
}

// Índice de la pista dentro del texto, con límite de palabra por los dos lados
// ("pie" no puede calzar dentro de "piel", que es la palabra más frecuente de
// todo este bloque; sin este freno el documento entero se iría a extremidades).
function posicionDePista(textoNorm, pista) {
  const re = new RegExp(`(^|[^a-z0-9ñ])${pista.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9ñ]|$)`);
  const m = re.exec(textoNorm);
  return m ? m.index : -1;
}

/**
 * ¿A qué segmento pertenece esta línea? La PRIMERA zona que nombra manda
 * (decisión del fundador: un párrafo que recorre varias zonas se ubica por
 * donde empieza). Empate en la misma posición → la pista más específica, para
 * que "labios mayores" gane sobre "labio".
 *
 * → id de SEGMENTOS_EXAMEN, o null si la línea no nombra ninguna zona.
 */
export function segmentoDeLinea(linea) {
  const t = normalizar(linea);
  if (!t.trim()) return null;
  let mejor = null;
  for (const [pista, segId] of indice().pistas) {
    const pos = posicionDePista(t, pista);
    if (pos < 0) continue;
    if (!mejor || pos < mejor.pos || (pos === mejor.pos && pista.length > mejor.pista.length)) {
      mejor = { pos, pista, segId };
    }
  }
  return mejor ? mejor.segId : null;
}

/**
 * Reordena los párrafos de un bloque de piel de cabeza a pies.
 *
 * Solo actúa si el bloque nombra al menos `minZonas` segmentos DISTINTOS: con
 * menos, el orden se lee bien igual y moverlo sorprende más de lo que ayuda.
 *
 * @param {string} texto  el bloque completo, con sus párrafos separados por \n
 * @param {Object} [opts]
 * @param {number} [opts.minZonas=4]
 * @returns {string} el mismo texto con los párrafos zonificados reordenados.
 *   Sin cambios (misma referencia de contenido) si no alcanza el umbral.
 */
export function ordenarCefaloCaudal(texto, { minZonas = MIN_ZONAS_ORDEN } = {}) {
  const original = String(texto || '');
  if (!original.trim()) return original;
  const lineas = original.split('\n');

  // Posiciones que ocupan las líneas con zona: son los ÚNICOS huecos que se
  // rebarajan. Las demás no se mueven nunca.
  const huecos = [];
  const conZona = [];
  lineas.forEach((linea, i) => {
    if (!linea.trim()) return;
    const seg = segmentoDeLinea(linea);
    if (!seg) return;
    huecos.push(i);
    conZona.push({ linea, orden: indice().orden.indexOf(seg), i });
  });

  const zonasDistintas = new Set(conZona.map((x) => x.orden)).size;
  if (zonasDistintas < minZonas) return original;

  // Estable: dentro del mismo segmento se respeta el orden en que ya venían.
  const ordenadas = [...conZona].sort((a, b) => (a.orden - b.orden) || (a.i - b.i));
  const salida = [...lineas];
  huecos.forEach((pos, k) => { salida[pos] = ordenadas[k].linea; });
  return salida.join('\n');
}
